import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Select, MenuItem, FormControl,
  InputLabel, TextField, Radio, Checkbox, IconButton, Button, Alert,
  Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Tune as TuneIcon,
  Build as BuildIcon,
  Folder as FolderIcon,
  AccountTree as AccountTreeIcon,
  Layers as LayersIcon,
  InfoOutlined as InfoOutlinedIcon,
  UnfoldMore as UnfoldMoreIcon,
  EditOutlined as EditOutlinedIcon
} from '@mui/icons-material';

import {
  DATE_FORMATS,
  DATA_TYPE_OPTIONS as BASE_DATA_TYPE_OPTIONS,
  detectDateFormat,
  validateDateValues,
  TypeConfigDialog as BaseTypeConfigDialog,
  FIELD_MAPPING_TYPE_MAP,
  DateFormatSelect
} from '../common/DataTypeComponents';

// Map the data types for FieldMappingBuilder
const DATA_TYPE_OPTIONS = BASE_DATA_TYPE_OPTIONS.map(opt => ({
  ...opt,
  value: Object.entries(FIELD_MAPPING_TYPE_MAP).find(([k, v]) => v === opt.value)?.[0] || opt.value
}));

const FieldMappingBuilder = ({ 
  schema, 
  mappings = [], 
  onChange,
  connectorType = 'bigquery',
  apiResponse = null,
  onError = null,
  timestampMode = 'finegrained'
}) => {
  const [fieldMappings, setFieldMappings] = useState([]);
  const [errors, setErrors] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [typeConfigDialog, setTypeConfigDialog] = useState({ open: false, fieldIndex: null });
  const [columnFormats, setColumnFormats] = useState({});
  const [customFormatDialog, setCustomFormatDialog] = useState({ open: false, fieldName: null });
  const [customPattern, setCustomPattern] = useState('');

  // Extract fields from REST API response
  const extractFieldsFromObject = (obj, prefix = '', context = 'root', parentArrayPath = null) => {
    const fields = [];
    const fieldSamples = {}; // To collect multiple samples for each field
    const fieldContexts = {}; // Track context for each field
    
    const processValue = (value, key, currentPath) => {
      if (value === null || value === undefined) {
        return 'null';
      } else if (typeof value === 'string') {
        // Enhanced date detection
        if (/^\d{4}-\d{2}-\d{2}/.test(value) || 
            /^\d{1,2}\/\d{1,2}\/\d{4}/.test(value) ||
            /^\d{1,2}-\d{1,2}-\d{4}/.test(value)) {
          return 'datetime_timestamp';
        }
        // Check if it's a numeric string
        if (/^-?\d+\.?\d*$/.test(value)) {
          return 'continuous_numerical';
        }
        return 'categorical_text';
      } else if (typeof value === 'number') {
        return Number.isInteger(value) ? 'discrete_numerical' : 'continuous_numerical';
      } else if (typeof value === 'boolean') {
        return 'binary_boolean';
      } else if (Array.isArray(value)) {
        return 'array';
      } else if (typeof value === 'object') {
        return 'object';
      }
      return 'mixed_complex';
    };
    
    // Helper to extract fields from a single object
    const extractFromSingleObject = (obj, prefix, currentContext = context, currentArrayPath = parentArrayPath) => {
      Object.entries(obj).forEach(([key, value]) => {
        const fieldPath = prefix ? `${prefix}.${key}` : key;
        const dataType = processValue(value, key, fieldPath);
        
        if (dataType !== 'object' && dataType !== 'array') {
          // Store samples for this field
          if (!fieldSamples[fieldPath]) {
            fieldSamples[fieldPath] = {
              values: [],
              types: []
            };
            fieldContexts[fieldPath] = {
              context: currentContext,
              arrayPath: currentArrayPath
            };
          }
          fieldSamples[fieldPath].values.push(value);
          fieldSamples[fieldPath].types.push(dataType);
        } else if (Array.isArray(value) && value.length > 0) {
          const firstItem = value[0];
          
          // Check if it's an array of objects
          if (typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)) {
            // This is an array of objects - extract fields from all objects
            // and treat them as if they were fields at this level
            const arrayFieldSamples = {};
            
            value.forEach(item => {
              if (typeof item === 'object' && item !== null) {
                Object.entries(item).forEach(([subKey, subValue]) => {
                  const subFieldPath = `${fieldPath}.${subKey}`;
                  const subDataType = processValue(subValue);
                  
                  if (subDataType !== 'object' && subDataType !== 'array') {
                    if (!arrayFieldSamples[subFieldPath]) {
                      arrayFieldSamples[subFieldPath] = {
                        values: [],
                        types: []
                      };
                      fieldContexts[subFieldPath] = {
                        context: 'array_item',
                        arrayPath: fieldPath,
                        parentContext: currentContext
                      };
                    }
                    arrayFieldSamples[subFieldPath].values.push(subValue);
                    arrayFieldSamples[subFieldPath].types.push(subDataType);
                  } else if (typeof subValue === 'object' && subValue !== null && !Array.isArray(subValue)) {
                    // Recursively process nested objects within array items
                    extractFromSingleObject(subValue, subFieldPath, 'array_item', fieldPath);
                  }
                });
              }
            });
            
            // Merge array field samples into main field samples
            Object.entries(arrayFieldSamples).forEach(([path, samples]) => {
              if (!fieldSamples[path]) {
                fieldSamples[path] = samples;
              } else {
                fieldSamples[path].values.push(...samples.values);
                fieldSamples[path].types.push(...samples.types);
              }
            });
          } else {
            // Array of primitives
            if (!fieldSamples[fieldPath]) {
              fieldSamples[fieldPath] = {
                values: [],
                types: []
              };
            }
            // Add all array values as samples
            value.forEach(item => {
              fieldSamples[fieldPath].values.push(item);
              fieldSamples[fieldPath].types.push(processValue(item));
            });
          }
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // Recursively process nested objects
          extractFromSingleObject(value, fieldPath, currentContext, currentArrayPath);
        }
      });
    };
    
    if (Array.isArray(obj) && obj.length > 0) {
      // Check if it's an array of objects with consistent structure
      const firstItem = obj[0];
      if (typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)) {
        // Extract fields from all objects in the array
        obj.forEach(item => {
          if (typeof item === 'object' && item !== null) {
            extractFromSingleObject(item, prefix);
          }
        });
      } else {
        // Handle array of primitives
        obj.forEach((item, index) => {
          const fieldPath = prefix ? `${prefix}[${index}]` : `[${index}]`;
          const dataType = processValue(item);
          
          if (dataType !== 'object' && dataType !== 'array') {
            fields.push({
              name: fieldPath,
              display: fieldPath,
              type: dataType,
              sampleValue: item
            });
          }
        });
      }
    } else if (typeof obj === 'object' && obj !== null) {
      extractFromSingleObject(obj, prefix);
    }
    
    // Convert fieldSamples to fields array
    Object.entries(fieldSamples).forEach(([fieldPath, samples]) => {
      // Determine the most common type
      const typeCount = {};
      samples.types.forEach(type => {
        typeCount[type] = (typeCount[type] || 0) + 1;
      });
      const mostCommonType = Object.entries(typeCount)
        .sort((a, b) => b[1] - a[1])[0][0];
      
      // Create a sample value string
      const uniqueValues = [...new Set(samples.values)];
      const sampleValue = uniqueValues.slice(0, 3).map(v => {
        if (typeof v === 'string' && v.length > 50) {
          return v.substring(0, 50) + '...';
        }
        return String(v);
      }).join(', ') + (uniqueValues.length > 3 ? ', ...' : '');
      
      const fieldContext = fieldContexts[fieldPath] || { context: 'root', arrayPath: null };
      
      fields.push({
        name: fieldPath,
        display: fieldPath,
        type: mostCommonType,
        sampleValue: sampleValue,
        allSamples: samples.values,
        depth: fieldPath.split('.').length - 1,
        context: fieldContext.context,
        arrayPath: fieldContext.arrayPath,
        parentContext: fieldContext.parentContext
      });
    });
    
    // Sort fields by path to keep related fields together
    fields.sort((a, b) => {
      const aPath = a.name.split('.');
      const bPath = b.name.split('.');
      
      // Compare path segments
      for (let i = 0; i < Math.min(aPath.length, bPath.length); i++) {
        if (aPath[i] !== bPath[i]) {
          return aPath[i].localeCompare(bPath[i]);
        }
      }
      
      // If one is a prefix of another, shorter comes first
      return aPath.length - bPath.length;
    });
    
    return fields;
  };

  // Wrapper for TypeConfigDialog to match the expected API
  const TypeConfigDialog = ({ open, onClose, fieldIndex, currentType, onTypeChange }) => {
    const fieldMapping = fieldMappings[fieldIndex];
    const fieldName = fieldMapping?.source_field || '';
    
    return (
      <BaseTypeConfigDialog
        open={open}
        onClose={onClose}
        columnName={fieldName}
        currentType={currentType}
        onTypeChange={(newType) => onTypeChange(fieldIndex, newType)}
        dataTypeOptions={DATA_TYPE_OPTIONS}
      />
    );
  };

  useEffect(() => {
    // For REST APIs, auto-populate fields from response if no mappings exist
    if (connectorType === 'rest' && apiResponse && !initialized) {
      const sourceFields = extractFieldsFromObject(apiResponse);
      const autoMappings = sourceFields
        .filter(field => !field.name.includes('.length') && field.type !== 'object')
        .map((field, index) => {
          const mapping = {
            source_field: field.name,
            target_key: field.name.split('.').pop().toLowerCase().replace(/[^a-z0-9]/g, '_'),
            data_type: field.type || 'categorical_text',
            is_timestamp: false,
            enabled: false,  // Start with all fields disabled
            depth: field.depth || 0,
            context: field.context,
            arrayPath: field.arrayPath
          };
          
          // Detect date format for datetime fields
          if (field.type === 'datetime_timestamp' && field.allSamples) {
            const detectedFormat = detectDateFormat(field.allSamples);
            if (detectedFormat) {
              setColumnFormats(prev => ({
                ...prev,
                [field.name]: detectedFormat
              }));
            }
          }
          
          return mapping;
        });
      
      setFieldMappings(autoMappings);
      setInitialized(true);
      onChange(autoMappings);  // Pass all fields (parent can detect datetime fields for auto-switching)
      validateMappings([]);  // Trigger validation to show initial error (pass empty for enabled check)
    } else if (connectorType !== 'rest' && mappings.length > 0) {
      // Only for non-REST APIs, update from mappings prop
      setFieldMappings(mappings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponse, connectorType, mappings.length]);

  // Get available source fields based on connector type and schema
  const getSourceFields = () => {
    // Handle REST API responses
    if (connectorType === 'rest' && apiResponse) {
      return extractFieldsFromObject(apiResponse);
    }
    
    if (!schema) return [];

    switch (connectorType) {
      case 'bigquery':
        if (schema.datasets && schema.datasets.length > 0) {
          return schema.datasets[0].tables.flatMap(table => 
            table.columns.map(col => ({
              name: `${table.table_id}.${col.name}`,
              display: `${table.table_id}.${col.name}`,
              type: col.universal_type || col.bigquery_type,
              description: col.description
            }))
          );
        }
        break;
      case 'postgres':
      case 'mysql':
        if (schema.tables) {
          return schema.tables.flatMap(table => 
            table.columns.map(col => ({
              name: `${table.table_name}.${col.name}`,
              display: `${table.table_name}.${col.name}`,
              type: col.universal_type || col.sql_type,
              nullable: col.nullable
            }))
          );
        }
        break;
      default:
        return [];
    }
    return [];
  };

  const sourceFields = getSourceFields();

  // Get sample values for a field from the API response
  const getSampleValues = (fieldPath) => {
    if (!apiResponse) return [];
    
    // For REST API fields extracted by our new logic, check if we have allSamples
    if (connectorType === 'rest') {
      const extractedFields = extractFieldsFromObject(apiResponse);
      const field = extractedFields.find(f => f.name === fieldPath);
      if (field && field.allSamples) {
        return field.allSamples;
      }
    }
    
    const parts = fieldPath.split('.');
    let values = [];
    
    // Helper to extract value from nested path
    const getValue = (obj, path) => {
      let current = obj;
      for (const part of path) {
        if (current && typeof current === 'object' && part in current) {
          current = current[part];
        } else {
          return undefined;
        }
      }
      return current;
    };
    
    // If apiResponse is an array, collect values from all items
    if (Array.isArray(apiResponse)) {
      values = apiResponse.map(item => getValue(item, parts)).filter(v => v !== undefined);
    } else {
      // If single object, just get the one value
      const value = getValue(apiResponse, parts);
      if (value !== undefined) {
        values = [value];
      }
    }
    
    return values;
  };

  const addMapping = () => {
    const newMapping = {
      source_field: '',
      target_key: '',
      data_type: 'continuous_numerical',
      is_timestamp: false,
      transformations: []
    };
    
    const newMappings = [...fieldMappings, newMapping];
    setFieldMappings(newMappings);
    onChange(newMappings);
  };

  const updateMapping = (index, updates) => {
    // If setting as timestamp, validate first
    if (updates.is_timestamp && connectorType === 'rest') {
      const mapping = fieldMappings[index];
      const field = sourceFields.find(f => f.name === mapping.source_field);
      
      if (field) {
        // Collect sample values for validation
        const values = getSampleValues(mapping.source_field);
        const formatInfo = columnFormats[mapping.source_field];
        const validation = validateDateValues(values, formatInfo);
        
        if (!validation.isValid) {
          const errorMessage = `Cannot set "${mapping.source_field}" as timestamp: Only ${validation.validCount}/${validation.totalValues} values are valid dates (${validation.validPercentage}%). ${validation.errors[0] || 'Invalid date format'}`;
          
          // Show error using the callback if provided
          if (onError) {
            onError(errorMessage);
          } else {
            // Fallback to console error and alert
            console.error(errorMessage);
            alert(errorMessage);
          }
          return; // Don't update the mapping
        }
      }
    }
    
    const newMappings = [...fieldMappings];
    newMappings[index] = { ...newMappings[index], ...updates };
    
    // Ensure only one timestamp field
    if (updates.is_timestamp) {
      newMappings.forEach((mapping, i) => {
        if (i !== index) {
          mapping.is_timestamp = false;
        }
      });
    }
    
    setFieldMappings(newMappings);
    // For REST APIs, only pass enabled fields to onChange
    if (connectorType === 'rest') {
      const enabledMappings = newMappings.filter(m => m.enabled);
      const mappingsWithFormats = enabledMappings.map(mapping => {
        if (mapping.data_type === 'datetime_timestamp' && columnFormats[mapping.source_field]) {
          return {
            ...mapping,
            dateFormat: columnFormats[mapping.source_field].format,
            datePattern: columnFormats[mapping.source_field].jsFormat
          };
        }
        return mapping;
      });
      onChange(mappingsWithFormats);
      validateMappings(enabledMappings);
    } else {
      onChange(newMappings);
      validateMappings(newMappings);
    }
  };

  const removeMapping = (index) => {
    const newMappings = fieldMappings.filter((_, i) => i !== index);
    setFieldMappings(newMappings);
    onChange(newMappings);
    validateMappings(newMappings);
  };

  const validateMappings = (mappings) => {
    const newErrors = [];
    const activeMappings = mappings.filter(m => m.enabled !== false);
    
    // Check if at least one field is selected
    if (activeMappings.length === 0) {
      newErrors.push('Please select at least one field to import');
    }
    
    // Only require timestamp field when in finegrained mode
    if (timestampMode === 'finegrained') {
      const hasTimestamp = activeMappings.some(m => m.is_timestamp);
      if (activeMappings.length > 0 && !hasTimestamp) {
        newErrors.push('At least one field must be marked as timestamp');
      }
    }
    
    // Check for duplicate target keys
    const targetKeys = activeMappings.map(m => m.target_key).filter(Boolean);
    const duplicates = targetKeys.filter((key, index) => targetKeys.indexOf(key) !== index);
    if (duplicates.length > 0) {
      newErrors.push(`Duplicate target keys: ${duplicates.join(', ')}`);
    }
    
    // Check for empty required fields
    activeMappings.forEach((mapping, index) => {
      if (!mapping.source_field) {
        newErrors.push(`Row ${index + 1}: Source field is required`);
      }
      if (!mapping.target_key) {
        newErrors.push(`Row ${index + 1}: Target key is required`);
      }
    });
    
    setErrors(newErrors);
  };

  // Auto-suggest target key based on source field
  const handleSourceFieldChange = (index, sourceField) => {
    const targetKey = sourceField.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '_') || '';
    updateMapping(index, { 
      source_field: sourceField,
      target_key: targetKey
    });
  };

  // Handle type configuration changes
  const handleTypeChange = (fieldIndex, newType) => {
    updateMapping(fieldIndex, { data_type: newType });
    
    // If datetime type selected, try to detect date format
    if (newType === 'datetime_timestamp') {
      const sourceName = fieldMappings[fieldIndex].source_field;
      let samples = [];
      
      // For REST APIs, get samples using our extraction logic
      if (connectorType === 'rest') {
        const extractedFields = extractFieldsFromObject(apiResponse);
        const field = extractedFields.find(f => f.name === sourceName);
        if (field && field.allSamples) {
          samples = field.allSamples;
        }
      } else {
        // For other connectors, use sampleValue
        const field = sourceFields.find(f => f.name === sourceName);
        if (field && field.sampleValue) {
          samples = [field.sampleValue];
        }
      }
      
      if (samples.length > 0) {
        const detectedFormat = detectDateFormat(samples);
        if (detectedFormat) {
          setColumnFormats(prev => ({
            ...prev,
            [sourceName]: detectedFormat
          }));
        }
      }
    }
    
    setTypeConfigDialog({ open: false, fieldIndex: null });
  };

  if (!schema && connectorType !== 'rest') {
    return (
      <Alert severity="info">
        Schema information is not available. Please test the connection first to load the schema.
      </Alert>
    );
  }
  
  if (connectorType === 'rest' && !apiResponse) {
    return (
      <Alert severity="info">
        Please test the REST API endpoint first to see available fields for mapping.
      </Alert>
    );
  }

  if (sourceFields.length === 0) {
    return (
      <Alert severity="warning">
        No fields found in the schema. Please check your data source configuration.
      </Alert>
    );
  }

  // Render REST API specific UI
  if (connectorType === 'rest' && apiResponse) {
    // Group fields by their array context
    const groupedFields = fieldMappings.reduce((groups, mapping) => {
      const field = sourceFields.find(f => f.name === mapping.source_field);
      if (!field) return groups;
      
      const context = field.context || 'root';
      const arrayPath = field.arrayPath || 'root';
      const groupKey = context === 'array_item' ? arrayPath : 'root';
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          context,
          arrayPath,
          fields: [],
          isArrayExpansion: context === 'array_item'
        };
      }
      
      groups[groupKey].fields.push({ mapping, field, index: fieldMappings.indexOf(mapping) });
      return groups;
    }, {});
    
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Field Mappings</Typography>
          <Typography variant="body2" color="text.secondary">
            {fieldMappings.filter(m => m.enabled).length} of {fieldMappings.length} fields selected
          </Typography>
        </Box>


        {/* Render grouped fields */}
        {Object.entries(groupedFields).map(([groupKey, group]) => (
          <Box key={groupKey} sx={{ mb: 3 }}>
            {/* Group Header */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 1,
                p: 1,
                bgcolor: group.isArrayExpansion ? 'primary.50' : 'success.50',
                borderRadius: 1
              }}
            >
              {group.isArrayExpansion ? (
                <>
                  <UnfoldMoreIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Array: {groupKey}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                    Each item creates a new row
                  </Typography>
                </>
              ) : (
                <>
                  <LayersIcon sx={{ color: 'success.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Shared Fields
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                    Same value for all rows
                  </Typography>
                </>
              )}
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ py: 1, width: timestampMode === 'finegrained' ? '16%' : '20%' }}>Source Field</TableCell>
                    {timestampMode === 'finegrained' && (
                      <TableCell align="center" sx={{ py: 1, width: '8%' }}>Timestamp</TableCell>
                    )}
                    <TableCell sx={{ py: 1, width: '15%' }}>Target Key</TableCell>
                    <TableCell sx={{ py: 1, width: '22%' }}>Data Type</TableCell>
                    <TableCell sx={{ py: 1, width: '15%' }}>Format</TableCell>
                    <TableCell sx={{ py: 1, width: timestampMode === 'finegrained' ? '24%' : '28%' }}>Sample Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.fields.map(({ mapping, field, index }) => {
                    const isNested = mapping.source_field.includes('.');
                    const depth = mapping.source_field.split('.').length - 1;
                    const indent = depth * 20;
                
                return (
                  <TableRow 
                    key={index} 
                    sx={{ 
                      backgroundColor: mapping.is_timestamp && mapping.enabled ? 'primary.50' : 'inherit'
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={mapping.enabled || false}
                        onChange={(e) => {
                          const newMappings = [...fieldMappings];
                          newMappings[index].enabled = e.target.checked;
                          setFieldMappings(newMappings);
                          // Only pass enabled fields to parent, but keep all fields in local state
                          const enabledMappings = newMappings.filter(m => m.enabled);
                          // Add date format information to enabled mappings
                          const mappingsWithFormats = enabledMappings.map(mapping => {
                            if (mapping.data_type === 'datetime_timestamp' && columnFormats[mapping.source_field]) {
                              return {
                                ...mapping,
                                dateFormat: columnFormats[mapping.source_field].format,
                                datePattern: columnFormats[mapping.source_field].jsFormat
                              };
                            }
                            return mapping;
                          });
                          onChange(mappingsWithFormats);
                          validateMappings(enabledMappings);
                        }}
                      />
                    </TableCell>
                    
                    <TableCell sx={{ py: 1 }}>
                      <Box sx={{ pl: `${indent}px`, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isNested && depth > 0 && (
                          <Box component="span" sx={{ color: '#999', fontSize: '0.9rem', width: 16 }}>
                            {depth === 1 ? '└' : '  └'}
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace', 
                              fontWeight: isNested ? 400 : 500,
                              fontSize: isNested ? '0.85rem' : '0.9rem',
                              color: mapping.enabled ? 'text.primary' : 'text.disabled'
                            }}
                          >
                            {isNested ? mapping.source_field.split('.').pop() : mapping.source_field}
                          </Typography>
                          {isNested && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                fontFamily: 'monospace',
                                opacity: 0.7
                              }}
                            >
                              {mapping.source_field}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    
                    {timestampMode === 'finegrained' && (
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Radio
                          size="small"
                          checked={mapping.is_timestamp || false}
                          onChange={() => updateMapping(index, { is_timestamp: true })}
                          disabled={!mapping.enabled}
                          color="primary"
                        />
                      </TableCell>
                    )}
                    
                    <TableCell sx={{ py: 1 }}>
                      <TextField
                        size="small"
                        value={mapping.target_key || ''}
                        onChange={(e) => updateMapping(index, { target_key: e.target.value })}
                        disabled={!mapping.enabled}
                        sx={{ width: '100%' }}
                        placeholder="field_name"
                      />
                      <Tooltip title="Optional friendly name shown across the platform (charts, segments, trainings) instead of the raw key — e.g. rename 'o' to 'Open'.">
                        <TextField
                          size="small"
                          value={mapping.display_name || ''}
                          onChange={(e) => updateMapping(index, { display_name: e.target.value })}
                          disabled={!mapping.enabled}
                          sx={{ width: '100%', mt: 0.5 }}
                          placeholder="Display name (optional)"
                          InputProps={{
                            startAdornment: <EditOutlinedIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.disabled' }} />,
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Send this field's values to the Events Engine (scored) instead of storing them as a signal time-series.">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.5 }}>
                          <Checkbox
                            size="small"
                            checked={!!mapping.is_event}
                            onChange={(e) => updateMapping(index, { is_event: e.target.checked, is_timestamp: e.target.checked ? false : mapping.is_timestamp })}
                            disabled={!mapping.enabled}
                            color="secondary"
                            sx={{ p: 0.25 }}
                          />
                          <Typography variant="caption" color="text.secondary">Event</Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    
                    <TableCell sx={{ py: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!mapping.enabled}
                        onClick={() => {
                          if (mapping.enabled) {
                            setTypeConfigDialog({ open: true, fieldIndex: index });
                          }
                        }}
                        startIcon={DATA_TYPE_OPTIONS.find(opt => opt.value === mapping.data_type)?.icon || DATA_TYPE_OPTIONS[0].icon}
                        endIcon={<TuneIcon sx={{ fontSize: 14 }} />}
                        sx={{ 
                          textTransform: 'none', 
                          fontSize: '0.8rem',
                          width: '100%',
                          justifyContent: 'flex-start',
                          '& .MuiButton-startIcon': { mr: 1 },
                          '& .MuiButton-endIcon': { ml: 'auto' }
                        }}
                      >
                        {DATA_TYPE_OPTIONS.find(opt => opt.value === mapping.data_type)?.label || 'Text/String'}
                      </Button>
                    </TableCell>
                    
                    <TableCell sx={{ py: 1 }}>
                      {mapping.data_type === 'datetime_timestamp' && mapping.enabled ? (
                        <DateFormatSelect
                          value={columnFormats[mapping.source_field]?.format}
                          onChange={(value) => {
                            if (value === 'none') {
                              // Remove format selection
                              setColumnFormats(prev => {
                                const newFormats = { ...prev };
                                delete newFormats[mapping.source_field];
                                return newFormats;
                              });
                              updateMapping(index, {});
                            } else if (value === 'custom') {
                              // Open custom format dialog
                              setCustomFormatDialog({ open: true, fieldName: mapping.source_field });
                              setCustomPattern('');
                              // Don't update mapping here, wait for the dialog result
                            } else {
                              const selectedFormat = DATE_FORMATS.find(f => f.format === value);
                              if (selectedFormat) {
                                setColumnFormats(prev => ({
                                  ...prev,
                                  [mapping.source_field]: selectedFormat
                                }));
                                updateMapping(index, {});
                              }
                            }
                          }}
                          disabled={!mapping.enabled}
                          includeCustom={true}
                          customIcon={<BuildIcon sx={{ fontSize: 16 }} />}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                          Not applicable
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell sx={{ py: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontFamily: 'monospace',
                          color: mapping.enabled ? 'text.primary' : 'text.secondary',
                          display: 'block',
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.75rem'
                        }}
                      >
                        {field?.sampleValue !== undefined ? JSON.stringify(field.sampleValue) : <em style={{ color: '#999' }}>empty</em>}
                      </Typography>
                    </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Fields Summary
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {fieldMappings.filter(m => m.enabled).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No fields selected yet
              </Typography>
            ) : (
              fieldMappings.filter(m => m.enabled).map((mapping, index) => {
                const typeOption = DATA_TYPE_OPTIONS.find(opt => opt.value === mapping.data_type);
                const field = sourceFields.find(f => f.name === mapping.source_field);
                const isFromArray = field?.context === 'array_item';
                return (
                  <Chip
                    key={index}
                    icon={isFromArray ? <UnfoldMoreIcon /> : typeOption?.icon}
                    label={`${mapping.target_key} (${typeOption?.label || mapping.data_type})`}
                    color={mapping.is_timestamp ? 'primary' : isFromArray ? 'secondary' : 'default'}
                    size="small"
                    variant={mapping.is_timestamp ? 'filled' : 'outlined'}
                  />
                );
              })
            )}
          </Box>
          
        </Box>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Type Configuration Dialog */}
        <TypeConfigDialog
          open={typeConfigDialog.open}
          onClose={() => setTypeConfigDialog({ open: false, fieldIndex: null })}
          fieldIndex={typeConfigDialog.fieldIndex}
          currentType={fieldMappings[typeConfigDialog.fieldIndex]?.data_type || 'categorical_text'}
          onTypeChange={handleTypeChange}
        />
        
        {/* Custom Date Format Dialog */}
        <Dialog 
          open={customFormatDialog.open} 
          onClose={() => setCustomFormatDialog({ open: false, fieldName: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Custom Date Format</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Enter a custom date format pattern (e.g., YYYY-MM-DD, DD/MM/YYYY HH:mm:ss)
              </Typography>
              <TextField
                fullWidth
                label="Format Pattern"
                value={customPattern}
                onChange={(e) => setCustomPattern(e.target.value)}
                placeholder="YYYY-MM-DD HH:mm:ss"
                sx={{ mt: 2, mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary">
                Common patterns: YYYY (year), MM (month), DD (day), HH (hour), mm (minute), ss (second)
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCustomFormatDialog({ open: false, fieldName: null })}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (customPattern && customFormatDialog.fieldName) {
                  setColumnFormats(prev => ({
                    ...prev,
                    [customFormatDialog.fieldName]: {
                      format: 'Custom Format',
                      example: customPattern,
                      pattern: new RegExp('.*'), // Accept any pattern
                      jsFormat: customPattern
                    }
                  }));
                  // Find the field index and trigger update
                  const fieldIndex = fieldMappings.findIndex(m => m.source_field === customFormatDialog.fieldName);
                  if (fieldIndex >= 0) {
                    updateMapping(fieldIndex, {});
                  }
                }
                setCustomFormatDialog({ open: false, fieldName: null });
              }}
              variant="contained"
              disabled={!customPattern}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Original UI for other connector types
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Field Mappings</Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={addMapping}
          variant="outlined"
          size="small"
        >
          Add Field
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Map source columns to target signal keys. One field must be marked as timestamp for time-series data.
      </Typography>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source Field</TableCell>
              <TableCell>Target Key</TableCell>
              <TableCell>Data Type</TableCell>
              <TableCell align="center">
                <Tooltip title="Mark as timestamp field for time-series data">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Timestamp?
                    <InfoIcon sx={{ ml: 0.5, fontSize: 16 }} />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fieldMappings.map((mapping, index) => (
              <TableRow key={index}>
                <TableCell>
                  <FormControl fullWidth size="small">
                    <Select
                      value={mapping.source_field || ''}
                      onChange={(e) => handleSourceFieldChange(index, e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select source field...</em>
                      </MenuItem>
                      {sourceFields.map((field) => (
                        <MenuItem key={field.name} value={field.name}>
                          <Box>
                            <Typography variant="body2">{field.display}</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {field.type && (
                                <Typography variant="caption" color="text.secondary">
                                  {field.type}
                                </Typography>
                              )}
                              {field.sampleValue !== undefined && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  • Sample: {JSON.stringify(field.sampleValue).slice(0, 50)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={mapping.target_key || ''}
                    onChange={(e) => updateMapping(index, { target_key: e.target.value })}
                    placeholder="signal_key_name"
                    helperText="Use lowercase with underscores"
                  />
                  <Tooltip title="Optional friendly name shown across the platform (charts, segments, trainings) instead of the raw key — e.g. rename 'o' to 'Open'.">
                    <TextField
                      fullWidth
                      size="small"
                      value={mapping.display_name || ''}
                      onChange={(e) => updateMapping(index, { display_name: e.target.value })}
                      sx={{ mt: 0.5 }}
                      placeholder="Display name (optional)"
                      InputProps={{
                        startAdornment: <EditOutlinedIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.disabled' }} />,
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Send this field's values to the Events Engine (scored) instead of storing them as a signal time-series.">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={!!mapping.is_event}
                        onChange={(e) => updateMapping(index, { is_event: e.target.checked, is_timestamp: e.target.checked ? false : mapping.is_timestamp })}
                        color="secondary"
                        sx={{ p: 0.25 }}
                      />
                      <Typography variant="caption" color="text.secondary">Event</Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                
                <TableCell>
                  <FormControl fullWidth size="small">
                    <Select
                      value={mapping.data_type || 'continuous_numerical'}
                      onChange={(e) => updateMapping(index, { data_type: e.target.value })}
                    >
                      {DATA_TYPE_OPTIONS.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Tooltip title={type.description} placement="right">
                            <span>{type.label}</span>
                          </Tooltip>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                
                <TableCell align="center">
                  <Radio
                    checked={mapping.is_timestamp || false}
                    onChange={() => updateMapping(index, { is_timestamp: true })}
                    color="primary"
                  />
                </TableCell>
                
                <TableCell align="center">
                  <IconButton
                    onClick={() => removeMapping(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            
            {fieldMappings.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No field mappings defined. Click "Add Field" to start mapping your data.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {fieldMappings.length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom>
            Mapping Summary
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {fieldMappings.map((mapping, index) => (
              <Chip
                key={index}
                label={`${mapping.target_key || 'unnamed'} (${mapping.data_type})`}
                color={mapping.is_timestamp ? 'primary' : 'default'}
                size="small"
                variant={mapping.is_timestamp ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Box>
      )}
      
      {/* Custom Date Format Dialog */}
      <Dialog 
        open={customFormatDialog.open} 
        onClose={() => setCustomFormatDialog({ open: false, fieldName: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Custom Date Format</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Enter a custom date format pattern (e.g., YYYY-MM-DD, DD/MM/YYYY HH:mm:ss)
            </Typography>
            <TextField
              fullWidth
              label="Format Pattern"
              value={customPattern}
              onChange={(e) => setCustomPattern(e.target.value)}
              placeholder="YYYY-MM-DD HH:mm:ss"
              sx={{ mt: 2, mb: 2 }}
            />
            <Typography variant="caption" color="text.secondary">
              Common patterns: YYYY (year), MM (month), DD (day), HH (hour), mm (minute), ss (second)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomFormatDialog({ open: false, fieldName: null })}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (customPattern && customFormatDialog.fieldName) {
                setColumnFormats(prev => ({
                  ...prev,
                  [customFormatDialog.fieldName]: {
                    format: 'Custom Format',
                    example: customPattern,
                    pattern: new RegExp('.*'), // Accept any pattern
                    jsFormat: customPattern
                  }
                }));
                // Find the field index and trigger update
                const fieldIndex = fieldMappings.findIndex(m => m.source_field === customFormatDialog.fieldName);
                if (fieldIndex >= 0) {
                  updateMapping(fieldIndex, {});
                }
              }
              setCustomFormatDialog({ open: false, fieldName: null });
            }}
            variant="contained"
            disabled={!customPattern}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Type Configuration Dialog */}
      <TypeConfigDialog
        open={typeConfigDialog.open}
        onClose={() => setTypeConfigDialog({ open: false, fieldIndex: null })}
        fieldIndex={typeConfigDialog.fieldIndex}
        currentType={typeConfigDialog.fieldIndex !== null ? fieldMappings[typeConfigDialog.fieldIndex]?.data_type : ''}
        onTypeChange={handleTypeChange}
      />
    </Box>
  );
};

export default FieldMappingBuilder;